import { Fragment, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LogDetail, LogQuery, LogRow, LogSort } from '@/types/logs';
import { formatRelativeTime } from '@/i18n/relativeTime';
import { LogRowDetail } from './LogRowDetail';

/** Cadence at which idle "Last seen" labels tick over (ms). */
const NOW_TICK_MS = 30_000;

interface Props {
  rows: LogRow[];
  sort: LogQuery['sort'];
  onSortChange(next: LogSort): void;
}

const LEVEL_LABEL_KEY: Record<LogRow['level'], string> = {
  error: 'admin.logs.table.levelError',
  warning: 'admin.logs.table.levelWarning',
  info: 'admin.logs.table.levelInfo',
};

export function LogsTable({ rows, sort, onSortChange }: Props): JSX.Element {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [detailCache, setDetailCache] = useState<Record<string, LogDetail>>({});

  const toggle = useCallback((id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const cacheDetail = useCallback((id: string, detail: LogDetail): void => {
    setDetailCache((prev) => ({ ...prev, [id]: detail }));
  }, []);

  // A re-render-driving clock so the "Last seen" relative labels stay
  // accurate when an admin leaves the page open. Without this, `Date.now()`
  // is only re-read when something else in the parent triggers a re-render,
  // so an idle page can drift arbitrarily out of date.
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), NOW_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="admin-table-wrap">
      <table className="admin-table admin-logs-table">
        <thead>
          <tr>
            <th scope="col" aria-label={t('admin.logs.table.level')} />
            <th scope="col">{t('admin.common.source')}</th>
            <th scope="col">{t('admin.logs.table.title')}</th>
            <th scope="col">{t('admin.logs.table.provider')}</th>
            <th scope="col">{t('admin.common.kind')}</th>
            <th scope="col" className="num">
              <button
                type="button"
                className="admin-logs-sortbtn"
                onClick={(): void => onSortChange('count')}
                aria-pressed={sort === 'count'}
              >
                {t('admin.logs.table.count')}{sort === 'count' ? ' ▾' : ''}
              </button>
            </th>
            <th scope="col">
              <button
                type="button"
                className="admin-logs-sortbtn"
                onClick={(): void => onSortChange('lastSeen')}
                aria-pressed={sort === 'lastSeen'}
              >
                {t('admin.logs.table.lastSeen')}{sort === 'lastSeen' ? ' ▾' : ''}
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="muted">{t('admin.logs.table.noRows')}</td>
            </tr>
          )}
          {rows.map((row) => {
            const isExpanded = expanded.has(row.id);
            return (
              <Fragment key={row.id}>
                <tr
                  className={`admin-logs-row admin-logs-row--${row.level}${isExpanded ? ' admin-logs-row--expanded' : ''}`}
                  onClick={(): void => toggle(row.id)}
                  aria-expanded={isExpanded}
                >
                  <td>
                    <span
                      className={`admin-logs-leveldot admin-logs-leveldot--${row.level}`}
                      role="img"
                      aria-label={t(LEVEL_LABEL_KEY[row.level])}
                    />
                  </td>
                  <td><span className={`admin-logs-source admin-logs-source--${row.source}`}>{row.source}</span></td>
                  <td className="admin-logs-title" title={row.title}>{row.title}</td>
                  <td>{row.provider ? <code>{row.provider}</code> : <span className="text-dim">—</span>}</td>
                  <td>{row.kind ? <code>{row.kind}</code> : <span className="text-dim">—</span>}</td>
                  <td className="num">{row.count}</td>
                  <td>{formatRelativeTime(t, row.lastSeen, nowMs)}</td>
                </tr>
                {isExpanded && (
                  <tr className="admin-redemptions-row">
                    <td colSpan={7}>
                      <LogRowDetail row={row} cached={detailCache[row.id]} onLoaded={cacheDetail} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
